<script setup lang="ts">
import PageHeader from '@/components/PageHeader.vue'
import { VUE2_CONTAINER_WIDTHS } from '@/util/vue2_layout'
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import ProgressSpinner from 'primevue/progressspinner'
import BrowseStrategiesFilters from './BrowseStrategiesFilters.vue'
import StrategyImageTile from './StrategyImageTile.vue'
import { getStrategies } from '@/strategies/api'
import { useUserStore } from '@/authentication/user.store'
import { useUserFeedbackContext } from '@/components/useUserFeedbackContext'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { selectionFromQuery, selectionToQuery } from './strategy_filters'
import type { StrategyFilterSelection, StrategySummary } from './strategy_types'

const MAX_UNLOCKED_FOR_NON_PREMIUM = 13
const KEYWORD_SEARCH_RESULT_SIZE = 150
const BROWSE_RESULT_SIZE = 400
const EMPTY_RESULTS_TEXT = 'No strategies found for the given criteria.'

const route = useRoute()
const router = useRouter()
const analytics = getAnalyticsTracker()
const { isPremium } = storeToRefs(useUserStore())
const { updateContext: updateFeedbackContext } = useUserFeedbackContext()

const strategies = ref<StrategySummary[]>([])
// The filters the rendered tiles were fetched with. selection follows the route, which
// has already moved on while a replacement search is in flight, so a click during that
// window would otherwise attribute a tile from the visible search to the pending one.
const renderedSelection = ref<StrategyFilterSelection>(selectionFromQuery({}))
const fetchingStrategies = ref(true)
const errorMessage = ref('')

let inFlightRequest: AbortController | null = null

const selection = computed(() => selectionFromQuery(route.query))

const showsEmptyResults = computed(
  () => !fetchingStrategies.value && !errorMessage.value && strategies.value.length === 0
)

// One status element for loading, count and empty alike: screen readers announce a
// change to a live region that is already in the DOM, not one inserted with its text.
const resultStatus = computed(() => {
  if (fetchingStrategies.value) {
    return 'Loading...'
  }
  if (showsEmptyResults.value) {
    return EMPTY_RESULTS_TEXT
  }
  if (!strategies.value.length) {
    return ''
  }
  return `${strategies.value.length} ${strategies.value.length === 1 ? 'result' : 'results'}`
})

function filtersEventData(from: StrategyFilterSelection = selection.value) {
  return {
    subject: from.subject || undefined,
    barrier: from.barrier || undefined,
    grades: from.grades || undefined,
    setting: from.setting || undefined,
    teachingType: from.teachingType || undefined,
    query: from.query || undefined
  }
}

function withAccessSortedFirst(results: StrategySummary[]) {
  if (isPremium.value) {
    return results
  }
  // non-premium users see unlocked strategies first, ahead of keyword relevance,
  // and only the first 13 of them are unlocked
  return [...results]
    .sort((a, b) => Number(Boolean(b.demo)) - Number(Boolean(a.demo)))
    .map((strategy, index) => ({
      ...strategy,
      demo: index < MAX_UNLOCKED_FOR_NON_PREMIUM && strategy.demo
    }))
}

async function loadStrategies() {
  inFlightRequest?.abort()
  inFlightRequest = new AbortController()
  const request = inFlightRequest

  fetchingStrategies.value = true
  errorMessage.value = ''

  try {
    const results = await getStrategies(
      {
        tags: [selection.value.subject, selection.value.barrier].filter(Boolean).join(','),
        setting: selection.value.setting || undefined,
        teachingType: selection.value.teachingType || undefined,
        grades: selection.value.grades?.join(',') || undefined,
        query: selection.value.query || undefined,
        size: selection.value.query ? KEYWORD_SEARCH_RESULT_SIZE : BROWSE_RESULT_SIZE
      },
      request.signal
    )
    if (request.signal.aborted) {
      return
    }
    strategies.value = withAccessSortedFirst(results)
    renderedSelection.value = selection.value
  } catch {
    if (request.signal.aborted) {
      return
    }
    strategies.value = []
    errorMessage.value = 'Error retrieving strategies. Please reload the page to try again.'
  } finally {
    if (!request.signal.aborted) {
      fetchingStrategies.value = false
      analytics.sendEvent(EVENT_NAMES.BROWSE_STRATEGIES.FILTERED_RESULTS, filtersEventData())
    }
  }
}

function updateFilter({ field, value }: { field: keyof StrategyFilterSelection; value: string | string[] | null }) {
  const nextSelection = { ...selection.value, [field]: value } as StrategyFilterSelection
  if (field === 'subject') {
    nextSelection.barrier = ''
  }
  // route.query is passed so the ?beta=true opt-in survives the navigation; the
  // analytics payload below deliberately does not carry it
  router.push({ name: 'BrowseStrategiesPage', query: selectionToQuery(nextSelection, route.query) })
}

function strategyClicked(strategy: StrategySummary) {
  const filters = filtersEventData(renderedSelection.value)
  analytics.sendEvent(
    EVENT_NAMES.BROWSE_STRATEGIES.CLICKED_RESULT,
    {
      uuid: strategy.uuid,
      title: strategy.title,
      ...filters,
      // Vue 2 spread the raw URL query into this one event, so downstream reports read
      // its grades as the comma-joined string rather than the array the other events carry
      grades: filters.grades?.join(',')
    },
    { cu: strategy.uuid, ct: strategy.title }
  )
}

watch(() => route.query, loadStrategies)

watch(
  () => selection.value.subject,
  (subject) => updateFeedbackContext({ subject }),
  { immediate: true }
)

onUnmounted(() => inFlightRequest?.abort())

loadStrategies()

analytics.sendEvent(EVENT_NAMES.BROWSE_STRATEGIES.VIEWED_PAGE, {
  ...filtersEventData(),
  ref: route.query.ref
})
</script>

<template>
  <div class="container min-h-screen" :class="VUE2_CONTAINER_WIDTHS">
    <PageHeader title="Browse Strategies" />
    <BrowseStrategiesFilters :selection="selection" @update:filter="updateFilter" />

    <div>
      <p v-if="errorMessage" role="alert" class="text-center text-text-xl text-gray-900">{{ errorMessage }}</p>
      <div
        class="flex items-center text-gray-700"
        :class="showsEmptyResults ? 'mt-8 justify-center text-center text-text-xl' : 'text-text-md'"
      >
        <ProgressSpinner v-if="fetchingStrategies" stroke-width="8" class="!ml-0 !mr-2 h-4 w-4 stroke-gray-700" aria-hidden="true" />
        <p role="status">{{ resultStatus }}</p>
      </div>

      <section :class="{ 'opacity-55': fetchingStrategies }">
        <ul class="grid list-none grid-cols-1 gap-x-[30px] md:grid-cols-2 lg:grid-cols-3">
          <li v-for="strategy of strategies" :key="strategy.uuid">
            <StrategyImageTile :strategy="strategy" @click="strategyClicked(strategy)" />
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>
